import React, { createContext, useContext, useState, useEffect } from 'react';
import { auth, db } from '../firebase';
import { onAuthStateChanged, signOut } from 'firebase/auth';
import { doc, getDoc } from 'firebase/firestore';

// Create AuthContext
const AuthContext = createContext();

// Custom hook to use the AuthContext
export const useAuth = () => useContext(AuthContext);

// AuthProvider component to wrap around your app
export const AuthProvider = ({ children }) => {
  const [user, setUser] = useState({
    isAuthenticated: false,
    role: null,
    uid: null,
  });
  const [loading, setLoading] = useState(true); // Loading state to handle initial auth check

  useEffect(() => {
    let isMounted = true;
    const stopLoadingTimer = window.setTimeout(() => {
      if (isMounted) {
        console.warn('Auth check timed out. Resetting to signed-out state.');
        setUser({
          isAuthenticated: false,
          role: null,
          uid: null,
          emailVerified: false,
        });
        setLoading(false);
      }
    }, 10000);

    const unsubscribe = onAuthStateChanged(auth, async (currentUser) => {
      window.clearTimeout(stopLoadingTimer);

      if (!isMounted) {
        return;
      }

      if (currentUser && currentUser.emailVerified) {
        try {
          const userDocRef = doc(db, 'users', currentUser.uid);
          const docSnapshot = await getDoc(userDocRef);

          if (!isMounted) {
            return;
          }

          if (docSnapshot.exists()) {
            const userData = docSnapshot.data();
            setUser({
              isAuthenticated: true,
              role: userData.role,
              uid: currentUser.uid,
              emailVerified: currentUser.emailVerified,
            });
          } else {
            // If user data not found, sign out
            await signOut(auth);
            setUser({
              isAuthenticated: false,
              role: null,
              uid: null,
              emailVerified: false,
            });
          }
        } catch (error) {
          console.error('Error fetching user data:', error);
          await signOut(auth).catch((signOutError) => {
            console.warn('Unable to clear auth state after profile fetch error:', signOutError);
          });

          if (!isMounted) {
            return;
          }

          setUser({
            isAuthenticated: false,
            role: null,
            uid: null,
            emailVerified: false,
          });
        }
      } else {
        setUser({
          isAuthenticated: false,
          role: null,
          uid: null,
          emailVerified: false,
        });
      }
      setLoading(false); // Finished checking auth state
    });

    return () => {
      isMounted = false;
      window.clearTimeout(stopLoadingTimer);
      unsubscribe();
    };
  }, []);

  // No longer needed since AuthContext manages auth state
  // const login = (role) => { ... };
  // const logout = () => { ... };

  return (
    <AuthContext.Provider value={{ user, loading }}>
      {children}
    </AuthContext.Provider>
  );
};
